import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Inbox, LogOut, PlusCircle, RefreshCw } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getToken = () => localStorage.getItem("saksham_cms_token");
const authHeaders = () => {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function CMS() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authed, setAuthed] = useState(false);

  // Verify token on mount
  useEffect(() => {
    const token = getToken();
    if (!token) { navigate("/login", { replace: true }); return; }
    axios.get(`${API}/auth/me`, { headers: authHeaders() })
      .then(() => setAuthed(true))
      .catch(() => {
        localStorage.removeItem("saksham_cms_token");
        localStorage.removeItem("saksham_cms_user");
        navigate("/login", { replace: true });
      });
  }, [navigate]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contact`, { headers: authHeaders() });
      setItems(res.data || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("saksham_cms_token");
        navigate("/login", { replace: true });
      }
    }
    finally { setLoading(false); }
  };

  useEffect(() => { if (authed) fetchItems(); }, [authed]); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = () => {
    localStorage.removeItem("saksham_cms_token");
    localStorage.removeItem("saksham_cms_user");
    toast.success("Signed out.");
    navigate("/");
  };

  if (!authed) {
    return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center" data-testid="cms-checking">
      <p className="text-sm text-[#4A4A4A]">Checking session…</p>
    </div>;
  }

  return (
    <div data-testid="page-cms" className="bg-[#FAFAFA] min-h-screen">
      <section className="pt-40 pb-10 border-b border-black/5">
        <div className="container-saksham flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#09B792] font-semibold mb-3">Content Management</p>
            <h1 className="font-serif text-5xl md:text-6xl font-medium leading-tight tracking-tight">Dashboard</h1>
            <p className="text-[#4A4A4A] mt-3 text-sm">Manage submissions and content.</p>
          </div>
          <button
            onClick={logout}
            data-testid="cms-logout"
            className="inline-flex items-center gap-2 border border-black/15 px-5 py-3 text-sm font-medium hover:bg-[#0A0A0A] hover:text-[#FBD223] transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </section>

      <section className="section-y">
        <div className="container-saksham grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-12">
          <Link
            to="/cms"
            className="bg-[#FBD223] p-8 group cursor-not-allowed opacity-90"
            data-testid="cms-card-blogs"
          >
            <PlusCircle className="text-[#0A0A0A]" size={24} />
            <h3 className="mt-5 font-serif text-2xl md:text-3xl font-medium leading-tight text-[#0A0A0A]">Upload a Blog</h3>
            <p className="mt-2 text-sm text-[#0A0A0A]/70">Coming soon — draft, publish and edit stories from the field.</p>
          </Link>
          <div className="bg-white border border-black/5 p-8" data-testid="cms-card-submissions-count">
            <FileText className="text-[#09B792]" size={24} />
            <h3 className="mt-5 font-serif text-2xl md:text-3xl font-medium leading-tight">Submissions</h3>
            <p className="mt-2 text-sm text-[#4A4A4A]">
              <span className="font-serif text-4xl text-[#0A0A0A]">{items.length}</span> total
            </p>
          </div>
          <div className="bg-white border border-black/5 p-8" data-testid="cms-card-published">
            <FileText className="text-[#4A4A4A]" size={24} />
            <h3 className="mt-5 font-serif text-2xl md:text-3xl font-medium leading-tight">Published Stories</h3>
            <p className="mt-2 text-sm text-[#4A4A4A]"><span className="font-serif text-4xl text-[#0A0A0A]">0</span> live</p>
          </div>
        </div>

        <div className="container-saksham">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#09B792] font-semibold mb-2">Inbox</p>
              <h2 className="font-serif text-3xl md:text-4xl font-medium">Contact Submissions</h2>
            </div>
            <button
              onClick={fetchItems}
              data-testid="cms-refresh"
              className="inline-flex items-center gap-2 border border-black/15 px-5 py-3 text-sm font-medium hover:bg-[#0A0A0A] hover:text-[#FBD223] transition-colors"
            >
              <RefreshCw size={14} /> Refresh
            </button>
          </div>

          <div className="bg-white border border-black/5 overflow-hidden">
            {loading ? (
              <div className="p-16 text-center text-[#4A4A4A]" data-testid="cms-loading">Loading…</div>
            ) : items.length === 0 ? (
              <div className="p-16 text-center" data-testid="cms-empty">
                <Inbox className="mx-auto text-[#09B792]" size={32} />
                <p className="mt-4 font-serif text-2xl">No submissions yet.</p>
                <p className="text-sm text-[#4A4A4A] mt-2">When someone submits the contact form, it&apos;ll show up here.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="cms-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-[10px] uppercase tracking-[0.18em]">Submitted</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.18em]">Name</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.18em]">Collaboration</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.18em]">Phone</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.18em]">Email</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.18em]">Message</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((it) => (
                      <TableRow key={it.id} data-testid={`cms-row-${it.id}`}>
                        <TableCell className="whitespace-nowrap text-xs text-[#4A4A4A]">
                          {new Date(it.created_at).toLocaleString("en-IN")}
                        </TableCell>
                        <TableCell className="font-medium">{it.full_name}</TableCell>
                        <TableCell>{it.organisation}</TableCell>
                        <TableCell className="font-mono text-sm">+91 {it.phone}</TableCell>
                        <TableCell className="text-sm">{it.email}</TableCell>
                        <TableCell className="max-w-xs text-sm text-[#4A4A4A]">{it.message || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
